import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import style from './confirmDetails.module.scss';
import fileIcon from '../../../Assets/images/file.svg';
import upload from '../../../Assets/images/bootstrap-upload.png';
import { CV_FILE_ALLOWED_FORMATS, MAX_CV_FILE_SIZE, validationMessages } from '../../../utils/constants';
import ValidationErrorLabel from '../../common/ValidationErrorLabel/ValidationErrorLabel';

function ConfirmDetails(props) {
  const [phone, setphone] = useState(null);
  const [Phone, setPhone] = useState('');
  const [PhoneCountryCode, setPhoneCode] = useState('');
  const [Cv, setCv] = useState(null);
  const [cvName, setCvName] = useState('');
  const initialErrorValidationValue = {
    phone: false,
    cv: false
  };
  const [errors, setErrors] = useState([]);
  const [phoneReqError, setPhoneReqError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [cvReq, setCvReq] = useState(false);
  const [errorValidation, setErrorValidation] = useState(initialErrorValidationValue);
  const { nextHandle, data, completedProfile, questions, userDataExist } = props;
  const user = useSelector((state) => state.user?.profile);

  const trQuestions = Object.values(questions).filter(Boolean);

  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const validateInputFile = (file) => {
    setCvReq(false);
    const currentErrors = [];
    if (!CV_FILE_ALLOWED_FORMATS.includes(file.name.split('.').pop()))
      currentErrors.push(validationMessage.CV_ALLOWED_FORMATS);
    if (file.size > MAX_CV_FILE_SIZE) currentErrors.push(validationMessage.CV_ALLOWED_FILE_SIZE);
    return currentErrors;
  };

  useEffect(() => {
    if (user && (completedProfile || userDataExist)) {
      setCvName(user?.CvName);

      if (user.Phone && user.PhoneCountryCode) {
        setPhone(user.Phone);
        setPhoneCode(user.PhoneCountryCode);
        setphone(`${user.PhoneCountryCode}${user.Phone}`);
      }
    }
    if (data.PhoneCountryCode && data.Phone) {
      setPhone(data.Phone);
      setPhoneCode(data.PhoneCountryCode);
      setphone(data.PhoneCountryCode + data.Phone);
    }
  }, [data, user, userDataExist]);

  const replaceFile = (e) => {
    setErrors([]);
    const validationErrors = validateInputFile(e.target.files[0]);

    if (validationErrors.length) {
      setErrors(validationErrors);
      return;
    }

    setCv(e.target.files[0]);
    setCvName(e.target.files[0].name);
  };

  const onDragOver = (ev) => ev.preventDefault();

  const validate = () => {
    const validationObj = {
      ...initialErrorValidationValue
    };
    if (!Phone) {
      validationObj.phone = true;
      setPhoneReqError(validationMessage.PHONE_NUMBER_REQ);
    }
    if (Phone && (PhoneCountryCode === 'undefined' || Phone.length < 4)) {
      validationObj.phone = true;
      setPhoneError(validationMessage.PHONE_NUMBER_INVALID);
    }
    if (!Cv) {
      validationObj.cv = true;
      setCvReq(true);
    }
    return validationObj;
  };

  return (
    <div className={style.mainWrapper}>
      <div className={style.mainContent}>
        <h4 className={style.header}>Confirm Details</h4>
        <h6 className={style.sub_header}>Here’s what we’ve pulled from your account.</h6>
        <label
          className={`${style.input_label} ${errorValidation.phone ? style.input_label_error : ''}`}
          htmlFor="phoneNumber"
        >
          Phone Number<span style={{ color: 'red' }}>{`${!completedProfile && !userDataExist ? '*' : ''}`}</span>
        </label>
        <PhoneInput
          inputStyle={{ width: '100%', marginBottom: '20px' }}
          value={phone}
          onChange={(newPhone, code) => {
            setPhoneReqError(false);
            setPhoneError(false);
            setphone(newPhone);
            setPhoneCode(`${code.dialCode ? '+' : ''}${code.dialCode}`);
            setPhone(`${newPhone}`.replace(new RegExp(`${code.dialCode}`), ''));
          }}
          id="phoneNumber"
          disabled={completedProfile || userDataExist}
        />
        {phoneError && <ValidationErrorLabel message={phoneError} />}
        {phoneReqError && <ValidationErrorLabel message={phoneReqError} />}
        <div style={{ paddingBottom: '20px' }} />
        <label
          className={`${style.input_label} ${errorValidation.cv ? style.input_label_error : ''}`}
          htmlFor="cvUpload"
        >
          Upload your CV<span style={{ color: 'red' }}>{`${!completedProfile && !userDataExist ? '*' : ''}`}</span>
        </label>
        <Form.Control
          id="cvUpload"
          placeholder=""
          aria-label="CV Upload"
          style={{ borderBottom: 0, borderLeft: 0, borderRight: 0, padding: 0, height: 0 }}
          disabled
          className={style.simple_text_field}
        />
        {!completedProfile && !userDataExist && cvName && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <img alt="file" className={style.file_icon} src={fileIcon} />
            <span className={style.file_name}>{cvName}</span>
          </div>
        )}
        {(completedProfile || userDataExist) && (
          <div className={style.uploadContainer}>
            <img alt="file" className={style.file_icon} src={fileIcon} />
            <span className={style.file_name}>{cvName}</span>
            <Button
              variant="outlined"
              color="primary"
              className={style.replace_button}
              component="label"
              sx={{
                textTransform: 'none'
              }}
            >
              Replace File
              <input type="file" accept=".pdf,.docx,.doc" hidden onChange={replaceFile} />
            </Button>
          </div>
        )}
      </div>
      {!completedProfile && !userDataExist && (
        <button style={{ border: 'none', background: 'none' }} onDragOver={onDragOver}>
          <label className="pb-3" htmlFor="file_picker" style={{ cursor: 'pointer' }}>
            <div className="">
              Drop files to upload or <b>browse</b>
            </div>
            <img className={style.uploadeImage} src={upload} style={{ marginTop: '20px' }} alt="file upload" />
            <input
              id="file_picker"
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={replaceFile}
              style={{ display: 'none' }}
            />
          </label>
        </button>
      )}
      <div className="pt-2 align-self-center">
        {cvReq && <ValidationErrorLabel message={validationMessage.CV_REQUIRED} />}
      </div>
      <div className="pt-2 align-self-center">
        {!!errors.length && errors.map((err) => <ValidationErrorLabel key={err} message={err} />)}
      </div>
      <button
        className={style.next_button}
        onClick={() => {
          const validationObj = validate();
          const validationWrongValues = Object.values(validationObj).filter(Boolean);
          if (completedProfile || userDataExist || !validationWrongValues.length) {
            nextHandle({ Phone, PhoneCountryCode, Cv });
          } else {
            const validationWrongEntries = Object.entries(validationObj).filter((v) => Boolean(v[1]));
            const newErrorValidationValue = {};
            validationWrongEntries.forEach((item) => {
              const [key, value] = item;
              newErrorValidationValue[key] = value;
            });
            setErrorValidation(newErrorValidationValue);
            setTimeout(() => setErrorValidation(initialErrorValidationValue), 2000);
          }
        }}
      >{`${trQuestions.length === 0 ? 'Submit' : 'Next'}`}</button>
    </div>
  );
}

export default ConfirmDetails;
